import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../../application/hooks/AuthContext';
import { loginSchema, LoginFormData } from './loginSchema';
import { AuthenticationError } from '../../../../common/domain/errors/AuthenticationError';

/**
 * LoginPage component
 * Provides authentication interface for administrators
 * Design aligned with Stitch: Nexus Admin design system
 */
export const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  });

  /**
   * Handle form submission
   */
  const onSubmit = async (data: LoginFormData): Promise<void> => {
    // Clear previous errors
    setSubmitError(null);
    clearError();

    try {
      await login(data.email, data.password);
      // On success, the authenticated redirect above will navigate to dashboard
    } catch (err) {
      // Error is already set in auth context
      // Set a user-friendly message
      if (err instanceof AuthenticationError) {
        setSubmitError(err.message);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    }
  };

  const displayError = submitError || error?.message;
  const loading = isLoading || isSubmitting;

  // Redirect to dashboard if already authenticated
  if (isAuthenticated && !isLoading) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9ff',
        px: 2,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 480,
          backgroundColor: '#ffffff',
          border: '1px solid #c3c5d9',
          borderRadius: '16px',
          boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box
              component="img"
              src="/assets/logo.png"
              alt="StyleConnect"
              sx={{
                width: 72,
                height: 72,
                display: 'block',
                mx: 'auto',
                objectFit: 'contain',
              }}
            />
            <Typography
              sx={{
                fontSize: '22px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                color: '#0b1c30',
                mt: 1.5,
              }}
            >
              StyleConnect
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: '#434656',
                mt: 0.5,
              }}
            >
              Admin Portal
            </Typography>
          </Box>

          {/* Error Alert */}
          {displayError && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: '#ffdad6',
                color: '#93000a',
                border: '1px solid #ffdad6',
                '& .MuiAlert-icon': { color: '#93000a' },
              }}
            >
              {displayError}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Email Field */}
              <TextField
                {...register('email')}
                label="Email"
                type="email"
                autoComplete="email"
                autoFocus
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={loading}
                slotProps={{
                  input: {
                    sx: {
                      backgroundColor: '#eff4ff',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                      overflow: 'hidden',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: errors.email ? '#EF4444' : '#c3c5d9',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: errors.email ? '#EF4444' : '#003ec7',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#003ec7',
                        boxShadow: '0 0 0 3px rgba(0, 76, 237, 0.15)',
                      },
                    },
                  },
                  inputLabel: {
                    sx: {
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                      color: '#737688',
                      '&.Mui-focused': {
                        color: '#003ec7',
                      },
                    },
                  },
                }}
              />

              {/* Password Field */}
              <TextField
                {...register('password')}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                fullWidth
                required
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={loading}
                slotProps={{
                  input: {
                    sx: {
                      backgroundColor: '#eff4ff',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                      overflow: 'hidden',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#c3c5d9',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#003ec7',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#003ec7',
                        boxShadow: '0 0 0 3px rgba(0, 76, 237, 0.15)',
                      },
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          sx={{ color: '#434656' }}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                  inputLabel: {
                    sx: {
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif',
                      color: '#737688',
                      '&.Mui-focused': {
                        color: '#003ec7',
                      },
                    },
                  },
                }}
              />

              {/* Remember & Forgot */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: -1,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      size="small"
                      sx={{
                        color: '#737688',
                        '&.Mui-checked': {
                          color: '#003ec7',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontFamily: 'Inter, sans-serif',
                        color: '#434656',
                      }}
                    >
                      Remember me
                    </Typography>
                  }
                />
                <Link
                  href="#"
                  sx={{
                    fontSize: '12px',
                    fontFamily: 'Inter, sans-serif',
                    color: '#003ec7',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 1,
                  backgroundColor: '#0052ff',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#003ec7',
                    boxShadow: 'none',
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
          </form>

          {/* Footer */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid rgba(115, 118, 136, 0.2)',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontFamily: 'Inter, sans-serif',
                color: '#737688',
              }}
            >
              © 2026 StyleConnect. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
              <Link
                href="#"
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  color: '#737688',
                  textDecoration: 'none',
                  '&:hover': { color: '#003ec7' },
                }}
              >
                Privacy Policy
              </Link>
              <Link
                href="#"
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  color: '#737688',
                  textDecoration: 'none',
                  '&:hover': { color: '#003ec7' },
                }}
              >
                Terms of Service
              </Link>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
